import {
  Box,
  Grid,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { StarOutlined, Visibility, StarBorder } from "@mui/icons-material";
import OrderPageCard from "../../components/cards/OrderPageCard";
import { orderFilters } from "../../utils/filterConfig";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PositionModal from "../../components/modals/OrderPositionModal";
import dayjs from "dayjs";
import DashboardTable from "../../components/NewTable/DashboardTable";
import Filter from "../../components/Filter";
import Export from "../../components/Export";
import duration from "dayjs/plugin/duration";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { formatter, toLocalTime } from "../../helper/functions";
import useOrders from "../../hooks/order/useOrders";
import ClientSearch from "../../components/search/clients/ClientSearch";

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.extend(duration);

export default function Orders() {
  const {
    theme,
    handleCloseModal,
    handlePositionChange,
    handlePositionSubmit,
    handleSearchInputChange,
    navigate,
    searchInput,
    selectedRow,
    setFiltrationData,
    filtrationData,
    setIsFilter,
    setIsSorting,
    rows,
    setRows,
    positionPlaceholder,
    positionBtn,
    setPage,
    positionModalOpen,
    positionModalTitle,
    total,
    retrieveData,
    setTotal,
  } = useOrders();


  const statusOptions = ["Booked", "Processing", "Delivered"];
  const positionOptions = ["Reject", "No Holds", "Review"];
  const orderColumns = [
    {
      id: "is_vip",
      label: "VIP",
      align: "center",
      renderCell: (row) => {
        return (
          <>
            {row.is_vip ? (
              <StarOutlined sx={{ color: "#F7C604", cursor: "auto" }} />
            ) : (
              <StarBorder sx={{ color: "#F7C604", cursor: "auto" }} />
            )}
          </>
        );
      },
      renderExport(row) {
        return row.is_vip ? "Yes" : "No";
      },
    },
    {
      id: "orderId",
      label: "Order ID",
      align: "center",
      renderCell: (row) => {
        return (
          <Typography
            onClick={() =>
              navigate(`/order-detail`, { state: { selectedOrder: row } })
            }
            sx={{
              fontSize: "12px",
              color: theme.palette.text.secondary,
              cursor: "pointer",
              ":active": {
                scale: "0.8",
              },
            }}
          >
            {row.orderId}
          </Typography>
        );
      },
    },
    {
      id: "invoiceNumber",
      label: "Invoice Number",
      align: "center",
    },
    {
      id: "dateBooked",
      label: "Date Booked",
      align: "center",
      renderCell: (row) => {
        return (
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.secondary }}
          >
            {toLocalTime(row.dateBooked)}
          </Typography>
        );
      },
      renderExport: (row) => toLocalTime(row.dateBooked),
    },
    {
      id: "dateBooked",
      label: "Waiting",
      align: "center",
      renderCell: (row) => {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const bookedTimeUTC = dayjs.tz(new Date(row.dateBooked), userTimeZone);
        if (!bookedTimeUTC.isValid()) return "Invalid date";

        const now = dayjs().tz(userTimeZone);

        const diffMs = now.diff(bookedTimeUTC);
        const diffDuration = dayjs.duration(diffMs);

        const hours = diffDuration.asHours();
        const minutes = diffDuration.minutes();

        const display =
          hours >= 1
            ? `${
                hours.toFixed(1).at(hours.toFixed(1).length - 1) === "0"
                  ? hours.toFixed(0)
                  : hours.toFixed(1)
              } Hrs`
            : `${minutes} ${minutes > 1 ? "mins" : "min"}`;
        return (
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.secondary }}
          >
            {display}
          </Typography>
        );
      },
      renderExport: (row) => {
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const bookedTimeUTC = dayjs.tz(new Date(row.dateBooked), userTimeZone);
        if (!bookedTimeUTC.isValid()) return "Invalid date";

        const now = dayjs().tz(userTimeZone);

        const diffMs = now.diff(bookedTimeUTC);
        const diffDuration = dayjs.duration(diffMs);

        const hours = diffDuration.asHours();
        const minutes = diffDuration.minutes();

        const display =
          hours >= 1
            ? `${
                hours.toFixed(1).at(hours.toFixed(1).length - 1) === "0"
                  ? hours.toFixed(0)
                  : hours.toFixed(1)
              } Hrs`
            : `${minutes} ${minutes > 1 ? "mins" : "min"}`;
        return display;
      },
    },
    { id: "bLocation", label: "B Location", align: "center" },
    { id: "customerId", label: "CID", align: "center" },
    { id: "tpu", label: "TPU/s", align: "center" },
    { id: "ppu", label: "PPU/s", align: "center" },
    {
      id: "amount",
      label: "Amount",
      align: "center",
      renderCell: (row) => {
        return (
          <Typography
            sx={{ fontSize: "12px", color: theme.palette.text.secondary }}
          >
            {formatter.format(row.amount)}
          </Typography>
        );
      },
      renderExport: (row) => formatter.format(row.amount),
    },
    {
      id: "currentStatus.status",
      label: "Current Status",
      align: "center",
      renderCell: (row) => {
        const activeStatus = row.currentStatus.status;
        const getStatusStyles = (status) => {
          switch (status) {
            case "Booked":
              return {
                background: "rgba(103, 60, 142, 0.35)",
                color: "rgba(103, 60, 142, 1)",
                fontSize: "10px",
              };
            case "Processing":
              return {
                background: "rgba(213, 229, 244, 1)",
                color: "rgba(76, 170, 224, 1)",
                fontSize: "10px",
              };
            case "Delivered":
              return {
                background: "rgba(217, 243, 234, 1)",
                color: "rgba(42, 189, 139, 1)",
                fontSize: "10px",
              };
            default:
              return {
                background: "#FFF0ED",
                color: "rgba(254, 133, 106, 1)",
              };
          }
        };
        const styles = getStatusStyles(activeStatus);

        return (
          <Button
            sx={{
              borderColor: styles.background,
              background: styles.background,
              color: styles.color,
              padding: "8px 18px",
              textTransform: "capitalize",
              cursor: "auto",
              border: "none",
              fontSize: "10px",
              width: "90px",
              outline: "none !important",
              "&:hover": {
                outline: styles.background,
                background: styles.background,
              },
              "&:focus": {
                outline: styles.background,
                background: styles.background,
              },
            }}
          >
            {activeStatus}
          </Button>
        );
      },
      renderExport: (row) => row.currentStatus.status,
    },
    {
      id: "position.status",
      label: "Position",
      align: "center",
      renderCell: (row) => {
        const activePosition = row.position.status;
        const currentStatus = row.currentStatus.status;
        const options =
          currentStatus !== "Delivered" && currentStatus !== "Cancelled"
            ? positionOptions
            : ["No Holds"];
        const getPositionStyles = (status) => {
          switch (status) {
            case "Reject":
              return {
                background: "rgba(255, 240, 237, 1)",
                color: "rgba(254, 133, 106, 1)",
                fontSize: "12px",
              };
            case "Review":
              return {
                background: "rgba(247, 198, 4, 0.2)",
                color: "rgba(247, 198, 4, 1)",
                fontSize: "12px",
              };
            case "No Holds":
              return {
                background: "rgba(217, 243, 234, 1)",
                color: "rgba(42, 189, 139, 1)",
                fontSize: "12px",
              };
            default:
              return {
                background: "#FFF0ED",
                color: "rgba(254, 133, 106, 1)",
              };
          }
        };
        const styles = getPositionStyles(activePosition);

        return (
          <>
            <Select
              value={activePosition}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                borderColor: styles.background,
                background: styles.background,
                color: styles.color,
                border: "none",
                fontSize: "12px",
                outline: "none !important",
                "& .MuiSelect-icon": {
                  color: styles.color,
                },
                "&:hover": {
                  outline: styles.background,
                },
                "&:focus": {
                  outline: styles.background,
                },
                "& .MuiSelect-select": {
                  paddingRight: "28px !important",
                  padding: "8px",
                  width: "60px",
                },
              }}
              onChange={(e) => handlePositionChange(row, e.target.value)}
            >
              {options.map((position) => (
                <MenuItem
                  key={position}
                  value={position}
                  sx={getPositionStyles(position)}
                >
                  {position}
                </MenuItem>
              ))}
            </Select>
          </>
        );
      },
      renderExport: (row) => row.position.status,
    },
    {
      id: "action",
      label: "View",
      align: "center",
      renderCell: (row) => {
        return (
          <IconButton
            sx={{ fontSize: "8px", color: "rgba(104, 60, 140, 1)" }}
            onClick={() =>
              navigate(`/order-detail`, { state: { selectedOrder: row } })
            }
          >
            <Visibility />
          </IconButton>
        );
      },
    },
  ];

  const getFilteredData = (data) => {
    if (!data.orderType) data.orderType = "";
    if (!data.position) data.position = "";
    if (!data.search) data.search = "";
    if (!data.orderStatus) data.orderStatus = "";
    setFiltrationData({
      ...filtrationData,
      ...data,
    });
    setIsFilter(true);
    setPage(0);
    setRows([]);
    setTotal(0);
  };

  return (
    <>
      <Grid container sx={{ padding: "0 18px" }}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: "30px",
              // color: "#673C8E",
              color: theme.palette.text.primary,
            }}
          >
            Orders
          </Typography>
        </Grid>

        <Grid item xs={12} sx={{ marginTop: "25px", display: "none" }}>
          <Grid container spacing={2}>
            <OrderPageCard
              title="Total Orders"
              count={rows.length > 0 ? "0" : 0}
              rate={rows.length > 0 ? 100 : 0}
            />
            <OrderPageCard
              title="New Orders"
              count={rows.length > 0 ? "0" : 0}
              rate={rows.length > 0 ? 70 : 0}
            />
            <OrderPageCard
              title="Refunds"
              count={rows.length > 0 ? "0" : 0}
              rate={rows.length > 0 ? 10 : 0}
            />
          </Grid>
        </Grid>
      </Grid>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          marginTop: "10px",
          paddingLeft: "18px",
          paddingRight: "18px",
        }}
      >
        <Box sx={{ width: "50%" }}>
          <ClientSearch
            placeholder="Search with Order ID, Invoice Number, Customer ID and Location"
            setFiltrationData={setFiltrationData}
            setRows={setRows}
            setTotal={setTotal}
            width="550px"
          />
        </Box>
        <div style={{ display: "flex", alignItems: "center" }}>
          {" "}
          <Filter
            filtrationData={filtrationData}
            onFilterData={getFilteredData} // filteration data
            onResetFilters={() => {
              setFiltrationData((prev) => ({
                search: prev.search,
                orderType: "",
                orderStatus: "",
                position: "",
                from: "",
                to: "",
                amountTo: "",
                amountFrom: "",
              }));
              setPage(0);
              setIsFilter(false);
              setRows([]);
              setTotal(0);
            }}
            type="orders"
            url="orders"
            config={orderFilters}
          />
          <Export columns={orderColumns} rows={rows} type="orders" />
        </div>
      </div>

      <DashboardTable
        columns={orderColumns}
        rows={rows}
        loadMore={{
          loadmore: () =>
            retrieveData().then((res) =>
              setRows((rows) => [...rows, ...res.result])
            ),
          total,
        }}
        emptyMessage="No orders yet !"
      />

      {positionModalOpen && selectedRow && (
        <PositionModal
          ifOrder={true}
          open={positionModalOpen}
          title={positionModalTitle}
          onSubmit={handlePositionSubmit}
          onClose={handleCloseModal}
          btnText={positionBtn}
          placeholder={positionPlaceholder}
        />
      )}
    </>
  );
}
